import { encodeQueryParams } from "@hex/common";
import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";

import {
  DEFAULT_TAO_PROJECT_OPEN_VIEWS,
  TAO_PROJECT_VIEW,
  type TaoProjectView,
} from "./taoProjectViews";

export type TaoProjectBranchQueryParams = {
  threadId?: string;
  s1?: TaoProjectView;
  s2?: TaoProjectView;
};

export function useTaoProjectOpenViews(): {
  changeView: (replace: boolean, view: TaoProjectView) => void;
  openPreviewTab: () => void;
  openViews: TaoProjectView[];
} {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const { needsRepair, paneState } = useMemo(
    () => parseTaoProjectPaneStateFromSearchParams(searchParams),
    [searchParams],
  );

  useEffect(() => {
    if (!needsRepair) {
      return;
    }
    const nextParams = new URLSearchParams(location.search);
    setTaoProjectSlotParams(nextParams, paneState);
    void navigate({ search: nextParams.toString() }, { replace: true });
  }, [location.search, navigate, needsRepair, paneState]);

  const openViews = useMemo(
    () => openViewsFromPaneState(paneState),
    [paneState],
  );

  const changeView = useCallback(
    (replace: boolean, view: TaoProjectView) => {
      const nextPaneState = getNextTaoProjectPaneState(
        paneState,
        view,
        replace,
      );
      const nextParams = new URLSearchParams(location.search);
      setTaoProjectSlotParams(nextParams, nextPaneState);
      void navigate({ search: nextParams.toString() }, { replace: true });
    },
    [location.search, navigate, paneState],
  );

  const openPreviewTab = useCallback(() => {
    const nextParams = new URLSearchParams(location.search);
    setTaoProjectSlotParams(nextParams, {
      slot1: TAO_PROJECT_VIEW.preview,
      slot2: null,
    });
    void navigate({ search: nextParams.toString() }, { replace: true });
  }, [location.search, navigate]);

  return useMemo(
    () => ({ changeView, openPreviewTab, openViews }),
    [changeView, openPreviewTab, openViews],
  );
}

export function taoProjectBranchQueryString(
  queryParams?: TaoProjectBranchQueryParams,
): string {
  const params = new URLSearchParams();
  appendTaoProjectBranchQueryParams(params, queryParams);
  return encodeQueryParams(Object.fromEntries(params.entries()));
}

interface TaoProjectPaneState {
  slot1: TaoProjectView;
  slot2: TaoProjectView | null;
}

const TAO_PROJECT_SLOT1_QUERY_PARAM = "s1";
const TAO_PROJECT_SLOT2_QUERY_PARAM = "s2";

const TAO_PROJECT_VIEW_VALUES: ReadonlySet<string> = new Set(
  Object.values(TAO_PROJECT_VIEW),
);

function isTaoProjectView(value: string): value is TaoProjectView {
  return TAO_PROJECT_VIEW_VALUES.has(value);
}

function getDefaultTaoProjectPaneState(): TaoProjectPaneState {
  const [slot1] = DEFAULT_TAO_PROJECT_OPEN_VIEWS;
  return { slot1, slot2: null };
}

function isDefaultTaoProjectPaneState(state: TaoProjectPaneState): boolean {
  const defaultState = getDefaultTaoProjectPaneState();
  return (
    state.slot1 === defaultState.slot1 && state.slot2 === defaultState.slot2
  );
}

function parseTaoProjectView(value: string | null): TaoProjectView | null {
  if (value == null || !isTaoProjectView(value)) {
    return null;
  }
  return value;
}

function openViewsFromPaneState(state: TaoProjectPaneState): TaoProjectView[] {
  if (state.slot2 != null) {
    return [state.slot1, state.slot2];
  }
  return [state.slot1];
}

function paneStateFromOpenViews(
  views: readonly TaoProjectView[],
): TaoProjectPaneState {
  const defaultState = getDefaultTaoProjectPaneState();
  const slot1 = views[0] ?? defaultState.slot1;
  const slot2 = views[1] ?? null;
  return { slot1, slot2 };
}

function parseTaoProjectPaneStateFromSearchParams(params: URLSearchParams): {
  paneState: TaoProjectPaneState;
  needsRepair: boolean;
} {
  const defaultState = getDefaultTaoProjectPaneState();
  const rawSlot1 = params.get(TAO_PROJECT_SLOT1_QUERY_PARAM);
  const rawSlot2 = params.get(TAO_PROJECT_SLOT2_QUERY_PARAM);

  if (rawSlot1 == null && rawSlot2 == null) {
    return { paneState: defaultState, needsRepair: false };
  }

  const parsedSlot1 = parseTaoProjectView(rawSlot1);
  const parsedSlot2 = parseTaoProjectView(rawSlot2);

  if (rawSlot1 == null && rawSlot2 != null) {
    return { paneState: defaultState, needsRepair: true };
  }

  if (parsedSlot1 == null) {
    return { paneState: defaultState, needsRepair: true };
  }

  if (rawSlot2 == null) {
    return {
      paneState: { slot1: parsedSlot1, slot2: null },
      needsRepair: false,
    };
  }

  if (parsedSlot2 == null || parsedSlot2 === parsedSlot1) {
    return {
      paneState: { slot1: parsedSlot1, slot2: null },
      needsRepair: true,
    };
  }

  return {
    paneState: { slot1: parsedSlot1, slot2: parsedSlot2 },
    needsRepair: false,
  };
}

function setTaoProjectSlotParams(
  params: URLSearchParams,
  paneState: TaoProjectPaneState,
): void {
  if (isDefaultTaoProjectPaneState(paneState)) {
    params.delete(TAO_PROJECT_SLOT1_QUERY_PARAM);
    params.delete(TAO_PROJECT_SLOT2_QUERY_PARAM);
    return;
  }

  params.set(TAO_PROJECT_SLOT1_QUERY_PARAM, paneState.slot1);
  if (paneState.slot2 != null) {
    params.set(TAO_PROJECT_SLOT2_QUERY_PARAM, paneState.slot2);
  } else {
    params.delete(TAO_PROJECT_SLOT2_QUERY_PARAM);
  }
}

function getNextTaoProjectPaneState(
  current: TaoProjectPaneState,
  view: TaoProjectView,
  replace: boolean,
): TaoProjectPaneState {
  const openViews = openViewsFromPaneState(current);

  if (replace) {
    return { slot1: view, slot2: null };
  }

  if (openViews.includes(view)) {
    if (openViews.length > 1) {
      return paneStateFromOpenViews(openViews.filter((v) => v !== view));
    }
    return current;
  }

  return paneStateFromOpenViews([...openViews, view]);
}

function appendTaoProjectBranchQueryParams(
  params: URLSearchParams,
  queryParams?: TaoProjectBranchQueryParams,
): void {
  if (queryParams == null) {
    return;
  }
  if (queryParams.threadId != null) {
    params.set("threadId", queryParams.threadId);
  }
  if (queryParams.s1 != null || queryParams.s2 != null) {
    setTaoProjectSlotParams(params, {
      slot1: queryParams.s1 ?? getDefaultTaoProjectPaneState().slot1,
      slot2: queryParams.s2 ?? null,
    });
  }
}
